import WebFont from 'webfontloader'

import isEmpty from '@/utils/isEmpty'

const root = document.documentElement
const _set = (key, val) => key && val && root.style.setProperty(key, val)

const default_font = `Lato`

// Map font weight names to numeric values
const FONT_WEIGHT_MAP = {
	thin: '100',
	extralight: '200',
	light: '300',
	regular: '400',
	medium: '500',
	semibold: '600',
	bold: '700',
	extrabold: '800',
	black: '900',
}

// Default weights to load if no specific weight is specified
// const DEFAULT_WEIGHTS = '400,500,600,700,800'
const DEFAULT_WEIGHTS = '400' // <- TODO: It's a note - This can cause visual issue - https://blueboxonline.slack.com/archives/C0815ENTTB7/p1755163905233829?thread_ts=1755161911.724839&cid=C0815ENTTB7

/**
 * Parse font name and extract base font family and specific weight
 * @param {string} fontString - Font string like "Open Sans - Bold" or "Roboto"
 * @returns {Object} - { family: string, weight: string|null, loadWeights: string }
 */
const parseFontString = (fontString) => {
	if (!fontString || typeof fontString !== 'string') {
		return { family: default_font, weight: null, loadWeights: DEFAULT_WEIGHTS }
	}

	const DEFAULT_INFO = { family: fontString, weight: null, loadWeights: DEFAULT_WEIGHTS }

	// Check if font has a weight specification (contains " - ")
	const parts = fontString.split(' - ')

	if (parts.length === 2) {
		const family = parts[0].trim()
		const weightName = parts[1].trim().toLowerCase()

		// Get numeric weight value from map
		const weight = FONT_WEIGHT_MAP[weightName]

		if (weight) {
			// If specific weight is found, load a range around it for flexibility
			// This ensures the font works even if slightly different weights are needed
			// const weightNum = parseInt(weight)
			const weights = []

			// Add the exact weight
			weights.push(weight)

			// Add nearby weights for better coverage
			// if (weightNum > 100) weights.push((weightNum - 100).toString())
			// if (weightNum < 900) weights.push((weightNum + 100).toString())

			// Always include 400 (regular) as fallback
			if (!weights.includes('400')) weights.push('400')

			// Sort and join
			const loadWeights = [...new Set(weights)]
				.map((w) => parseInt(w))
				.sort((a, b) => a - b)
				.join(',')

			return { family, weight, loadWeights }
		}

		// If weight name not recognized, treat as part of font family
		return DEFAULT_INFO
	}

	// No weight specification, use font as-is
	return DEFAULT_INFO
}

/**
 * Load a font with proper weight handling
 * @param {string} fontName - Font name potentially with weight specification
 * @returns {Object} - Parsed font information
 */
export const loadFont = (fontName) => {
	const fontInfo = parseFontString(fontName)

	try {
		WebFont.load({
			google: {
				families: [`${fontInfo.family}:${fontInfo.loadWeights}&display=swap`],
			},
			// Add error handling callbacks
			fontactive: (familyName, fvd) => {
				console.log(`Font loaded: ${familyName} ${fvd}`)
			},
			fontinactive: (familyName, fvd) => {
				console.warn(`Failed to load font: ${familyName} ${fvd}`)
				// Fallback to default font if loading fails
				if (familyName !== default_font) loadFont(default_font)
			},
		})
	} catch (error) {
		console.error('Error loading font:', error)
		// Fallback to default font on error
		if (fontInfo.family !== default_font) loadFont(default_font)
	}

	return fontInfo
}

/**
 * Update theme via CSS variables with enhanced font handling
 * @param {Object} theme - Theme configuration object
 */
export const updateThemeViaCssVar = (theme) => {
	if (isEmpty(theme)) return

	// Set color variables
	_set('--primary-color', theme.primary_color)
	_set('--secondary-color', theme.secondary_color)
	_set('--accent-color', theme.third_color)

	_set('--font-primary-color', theme.font_primary_color)
	_set('--font-secondary-color', theme.font_secondary_color)
	_set('--font-accent-color', theme.font_third_color)
	_set('--main-text-color', theme.font_color)

	_set('--body-bg-color', theme.background_color)

	// Handle main font
	const font_main = theme.font || default_font
	const mainFontInfo = loadFont(font_main)
	const mainFamily = mainFontInfo.family

	// IMPORTANT: Use the ORIGINAL string (e.g., "Titillium Web - Bold") for CSS
	// This preserves the user's selection for display purposes
	_set('--main-font', `${mainFamily}, sans-serif`)

	// If a specific weight was requested, set it as a separate variable
	if (mainFontInfo.weight) {
		_set('--main-font-weight', mainFontInfo.weight)
	} else {
		_set('--main-font-weight', '400') // Default to regular
	}

	// Handle accent font
	const font_accent = theme.font_accent || default_font
	const accentFontInfo = parseFontString(font_accent)
	const accentFamily = accentFontInfo.family

	// Only load accent font if it has a different base family
	if (mainFamily !== accentFamily) loadFont(font_accent)

	// Always set the accent font variables
	_set('--accent-font', `${accentFamily}, sans-serif`)
	_set('--accent-font-weight', accentFontInfo.weight || '400')
}

export { parseFontString, FONT_WEIGHT_MAP, DEFAULT_WEIGHTS }
